import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  TextField,
  Slide,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import useAPI from '../../../hooks/useAPI';
import { BURL } from '../../../services/base';
import DeleteIcon from '@mui/icons-material/Delete';
import { pptTypes } from '../../../services/utils';
import SmallLoader from '../../widgets/SmallLoader';
import { RemoveRedEye } from '@mui/icons-material';
import { HorizontalLinearAssignAdminStepper } from '../components/linearStepper';
import { useFormik } from 'formik';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

function PropertyDetails() {
  const { id } = useParams();
  const typeList = pptTypes;
  const { POST, deleteRequest, PATCH, DELETE } = useAPI();
  const token = useMemo(() => localStorage.getItem('token'), []);
  const fileInputRef = useRef(null);

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [removalConfirmationOpen, setRemovalConfirmationOpen] =
    React.useState(false);
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [roles, setRoles] = useState([]);

  const formik = useFormik({
    initialValues: {
      adminId: Math.floor(Math.random() * 90) + 10,
      roleId: '',
      propertyId: id,
    },
  });

  const { values, handleChange, handleSubmit, resetForm } = formik;

  const [state, setState] = React.useState({
    open: false,
    vertical: 'top',
    horizontal: 'center',
  });

  // dialogs: null | 'ADD_ADMIN' | 'CONFIRM_REMOVE_ADMIN'
  const [dialog, setDialog] = useState(null);
  const [unitId, setUnitId] = useState(null);
  const [adminPropertyRoleId, setAdminPropertyRoleId] = useState(null);

  const [unitDetails, setUnitDetails] = useState({
    name: '',
    description: '',
    propertyId: 0,
    unitId: 0,
  });

  const [unitData, setUnitData] = useState({
    name: '',
    description: '',
    propertyId: '',
    rooms: [],
  });

  // toaster
  const [showToaster, setShowToaster] = useState(false);
  const [toasterType, setToasterType] = useState('success');
  const [message, setMessage] = useState('');

  // admins editing
  const [editableIndex, setEditableIndex] = useState(null);
  const [adminRoles, setAdminRoles] = useState([]);
  const [adminDetails, setAdminDetails] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [contentModalOpen, setContentModalOpen] = useState(false);
  const [unitModalOpen, setUnitModalOpen] = useState(false);
  const [unitUpdateModalOpen, setUnitUpdateModalOpen] = useState(false);

  const snackbarAnchor = useMemo(
    () => ({ vertical: 'top', horizontal: 'right' }),
    []
  );

  // Property
  const [property, setProperty] = useState({
    name: '',
    type: '',
    address: {
      address: '',
      street: '',
      state: '',
      city: '',
      country: '',
      zip: '',
    },
    units: [],
    adminPropertyRoles: [],
  });

  const [updateProperty, setUpdateProperty] = useState({
    name: '',
    address: '',
    type: '',
    street: '',
    state: '',
    city: '',
    country: '',
    zip: '',
    propertyId: '',
    addressId: '',
    adminPropertyRoles: [],
  });

  // RESOURCES (PROPERTY IMAGES)
  const [resources, setResources] = useState([]);
  const [previewMainResourceId, setPreviewMainResourceId] = useState(null);

  // IMAGE SELECT + UPLOAD
  const [selectedImageFile, setSelectedImageFile] = useState(null); // File to upload
  const [imagePreview, setImagePreview] = useState(null); // local preview URL
  const [uploadingImage, setUploadingImage] = useState(false);

  // edit resource modal
  const [editResourceOpen, setEditResourceOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [resourceForm, setResourceForm] = useState({
    order: 0,
    description: '',
    isCover: false,
    isPublic: true,
  });
  const [savingResource, setSavingResource] = useState(false);

  // ✅ NEW: delete resource modal
  const [deleteResourceOpen, setDeleteResourceOpen] = useState(false);
  const [deletingResource, setDeletingResource] = useState(null);
  const [deletingResourceLoading, setDeletingResourceLoading] = useState(false);

  // Toaster Helpers
  const handleToasterClose = useCallback(() => {
    setShowToaster(false);
  }, []);

  const openToaster = useCallback((type, msg) => {
    setToasterType(type);
    setMessage(msg);
    setShowToaster(true);
  }, []);

  const fetchPropertyDetails = useCallback(async () => {
    const response = await axios.get(`${BURL}/admin/properties/${id}`, {
      params: {
        withUnits: true,
        withAdmins: true,
        withAddress: true,
        withRooms: true,
      },
      headers: { Authorization: `Bearer ${token}` },
    });

    const prop = response?.data?.data;

    setProperty(prop);
    setUpdateProperty({
      name: prop?.name ?? '',
      address: prop?.address?.address ?? '',
      type: prop?.type ?? '',
      street: prop?.address?.street ?? '',
      state: prop?.address?.state ?? '',
      city: prop?.address?.city ?? '',
      country: prop?.address?.country ?? '',
      zip: prop?.address?.zip ?? '',
      propertyId: prop?.id ?? '',
      addressId: prop?.addressId ?? '',
      adminPropertyRoles: prop?.adminPropertyRoles ?? [],
    });
  }, [id, token]);

  const fetchPropertyResources = useCallback(async () => {
    const t = localStorage.getItem('token');
    const res = await axios.get(`${BURL}/admin/properties/${id}/resources`, {
      params: { type: 'image' },
      headers: { Authorization: `Bearer ${t}` },
    });

    const list = res?.data?.resources ?? [];

    // sort by order asc, then newest
    const sorted = [...list].sort((a, b) => {
      const ao = Number.isFinite(a?.order) ? a.order : 999999;
      const bo = Number.isFinite(b?.order) ? b.order : 999999;
      if (ao !== bo) return ao - bo;

      const da = new Date(a?.createdAt ?? 0).getTime();
      const db = new Date(b?.createdAt ?? 0).getTime();
      return db - da;
    });

    setResources(sorted);

    // if the currently previewed image no longer exists, clear it
    setPreviewMainResourceId((prev) => {
      if (!prev) return null;
      const stillExists = sorted.some((r) => r.id === prev);
      return stillExists ? prev : null;
    });
  }, [id]);

  const getAllRoles = useCallback(async () => {
    const t = localStorage.getItem('token');
    const response = await axios.get(`${BURL}/app/roles`, {
      headers: { Authorization: `Bearer ${t}` },
    });
    setRoles(response?.data?.data ?? []);
  }, []);

  const handleAdminDataChange = useCallback((e, i) => {
    const { name, value } = e.target;

    setAdminRoles((prev) => {
      const updated = [...prev];

      if (name === 'name') {
        updated[i] = {
          ...updated[i],
          admin: {
            ...updated[i].admin,
            user: {
              ...updated[i].admin.user,
              firstname: value,
            },
          },
        };
      }

      if (name === 'role') {
        updated[i] = {
          ...updated[i],
          role: {
            ...updated[i].role,
            id: value,
          },
        };
      }

      setAdminDetails({
        adminPropertyRoleId: updated[i]?.adminPropertyRoleId,
        role: { id: updated[i]?.role?.id },
      });

      return updated;
    });
  }, []);

  const handleAdminUpdate = useCallback(async () => {
    if (!adminDetails?.adminPropertyRoleId || !adminDetails?.role?.id) {
      openToaster('error', 'Missing admin role details.');
      return;
    }

    setConfirmLoading(true);
    try {
      const url = 'admin/roles';
      const t = localStorage.getItem('token');
      const body = {
        adminPropertyRoleId: adminDetails.adminPropertyRoleId,
        roleId: adminDetails.role.id,
      };

      const response = await PATCH(url, body, t);

      if (response?.status) {
        openToaster('success', response.message || 'Admin updated successfully');
        setEditableIndex(null);
        await fetchPropertyDetails();
      } else {
        openToaster(
          'error',
          response.message ??
            'An error occurred while editing admin details, try again please.'
        );
      }
    } catch (err) {
      openToaster('error', err?.response?.data?.message ?? `${err}`);
    } finally {
      setConfirmLoading(false);
    }
  }, [PATCH, adminDetails, fetchPropertyDetails, openToaster]);

  const confirmRemoveAdmin = useCallback((aprId) => {
    setAdminPropertyRoleId(aprId);
    setDialog('CONFIRM_REMOVE_ADMIN');
  }, []);

  const handleRemoveAdmin = useCallback(async () => {
    if (!adminPropertyRoleId) return;

    setConfirmLoading(true);
    try {
      const t = localStorage.getItem('token');
      const url = `admin/roles/${adminPropertyRoleId}`;
      const response = await DELETE(url, t);

      if (response?.status) {
        openToaster('success', response.message || 'Admin removed successfully');
        setDialog(null);
        await fetchPropertyDetails();
      } else {
        const msg =
          response?.response?.data?.message ??
          response?.message ??
          'An error occurred while removing admin, try again please.';
        openToaster('error', msg);
      }
    } catch (err) {
      openToaster('error', err?.response?.data?.message ?? `${err}`);
    } finally {
      setConfirmLoading(false);
    }
  }, [DELETE, adminPropertyRoleId, fetchPropertyDetails, openToaster]);

  const handleUnitUpdateModal = (unit) => {
    setUnitUpdateModalOpen(true);
    setUnitDetails({
      name: unit.name,
      description: unit.description,
      propertyId: unit.propertyId,
      unitId: unit.id,
    });
  };

  const handleConfirmation = (unitId) => {
    setConfirmationOpen(true);
    setUnitId(unitId);
  };

  // cleanup object URL
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const closeAllDialogs = useCallback(() => {
    setDialog(null);
    setErrorText('');
  }, []);

  // Load on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setErrorText('');
      try {
        await Promise.all([
          fetchPropertyDetails(),
          fetchPropertyResources(),
          getAllRoles(),
        ]);
      } catch (err) {
        console.error(err);
        if (mounted) {
          setErrorText(
            err?.response?.data?.message ??
              'Something went wrong. Please try again.'
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [fetchPropertyDetails, fetchPropertyResources, getAllRoles]);

  const explicitCoverIndex = useMemo(() => {
    return resources.findIndex((r) => r?.isCover);
  }, [resources]);

  const coverSlotIndex = useMemo(() => {
    if (!resources.length) return -1;
    return explicitCoverIndex >= 0 ? explicitCoverIndex : 0;
  }, [resources.length, explicitCoverIndex]);

  // If user clicked a thumbnail, use it as main preview
  const previewIndex = useMemo(() => {
    if (!previewMainResourceId) return -1;
    return resources.findIndex((r) => r.id === previewMainResourceId);
  }, [resources, previewMainResourceId]);

  const mainImageSrc =
    imagePreview ||
    (previewIndex >= 0 ? resources[previewIndex].resource : null) ||
    (coverSlotIndex >= 0 ? resources[coverSlotIndex].resource : null) ||
    '/house_img.png';

  const currentMainResource =
    previewIndex >= 0
      ? resources[previewIndex]
      : coverSlotIndex >= 0
      ? resources[coverSlotIndex]
      : null;

  // Thumbnails slots logic
  const thumbSlots = useMemo(() => {
    if (!resources.length) return [null, null, null];
    const withoutCover =
      coverSlotIndex >= 0
        ? resources.filter((_, idx) => idx !== coverSlotIndex)
        : resources;
    const thumbs = withoutCover.slice(0, 3);
    while (thumbs.length < 3) thumbs.push(null);
    return thumbs;
  }, [resources, coverSlotIndex]);

  const handleUpdateChange = useCallback((e) => {
    const { name, value } = e.target;
    setUpdateProperty((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleUnitChange = (e) => {
    const { name, value } = e.target;
    setUnitData((prevUnitData) => ({
      ...prevUnitData,
      [name]: value,
    }));
    setUnitDetails((prevUnitDetails) => ({
      ...prevUnitDetails,
      [name]: value,
    }));
  };

  const onSaveProperty = useCallback(
    async (e) => {
      e?.preventDefault?.();
      setLoading(true);
      setErrorText('');
      setSuccess('');

      try {
        const response = await POST('admin/properties/update', updateProperty);

        if (response?.status) {
          setSuccess(response.message || 'Property updated successfully');
          setIsEditing(false);
          openToaster(
            'success',
            response.message || 'Property updated successfully'
          );
          await fetchPropertyDetails();
        } else {
          const msg = response?.message || 'An unknown error occurred';
          setErrorText(msg);
          openToaster('error', msg);
        }
      } catch (err) {
        const msg = err?.response?.data?.message ?? `${err}`;
        setErrorText(msg);
        openToaster('error', msg || 'Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [POST, updateProperty, fetchPropertyDetails, openToaster]
  );

  const loadImage = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    // while selecting a new image, clear thumbnail preview
    setPreviewMainResourceId(null);
  }, []);

  const uploadPropertyImage = useCallback(async () => {
    if (!selectedImageFile) {
      openToaster('error', 'Please select an image first.');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('resources', selectedImageFile);
      formData.append('type', 'image');

      const t = localStorage.getItem('token');

      const res = await axios.post(
        `${BURL}/admin/properties/${id}/resources`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${t}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      openToaster('success', res?.data?.message || 'Image uploaded successfully');

      // refresh resources so UI updates
      await fetchPropertyResources();

      // clear local selection
      setSelectedImageFile(null);
      setImagePreview(null);
      setPreviewMainResourceId((prev) => null);
    } catch (err) {
      const msg =
        err?.response?.data?.message || 'Upload failed. Please try again.';
      openToaster('error', msg);
    } finally {
      setUploadingImage(false);
    }
  }, [selectedImageFile, id, fetchPropertyResources, openToaster]);

  const formatDate = (dateString) => {
    const dateObj = new Date(dateString);
    const day = String(dateObj.getUTCDate()).padStart(2, '0');
    const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-indexed
    const year = dateObj.getUTCFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleAdminRemovalConfirmation = (adminPropertyRoleId) => {
    setRemovalConfirmationOpen(true);
    setAdminPropertyRoleId(adminPropertyRoleId);
  };

  const handleClose = () => {
    setModalOpen(false);
    setUnitModalOpen(false);
    setUnitUpdateModalOpen(false);
    setConfirmLoading(false);
  };

  const addNewUnit = async () => {
    setErrorText('');
    setConfirmLoading(true);
    unitData.propertyId = property.id;

    if (unitData.name === '' || unitData.description === '') {
      setErrorText('Name and description are required');
      setConfirmLoading(false);
    }

    try {
      const response = await POST('admin/properties/units/new', unitData);

      if (response?.status) {
        setSuccess(response.message);
        setShowToaster(true);
        setToasterType('success');
        setMessage(response.message || 'Unit added successfully');
        setUnitModalOpen(false);
        fetchPropertyDetails();
      } else {
        setShowToaster(true);
        setToasterType('error');
        setMessage(
          response?.response?.data?.message ??
            'An error occurred while adding unit, try again please.'
        );
      }
    } catch (err) {
      setShowToaster(true);
      setToasterType('error');
      setMessage(`${err} Something went wrong. Please try again.`);
    } finally {
      setConfirmLoading(false);
    }
  };

  const updateUnit =
    (newState) =>
    async () => {
      setConfirmLoading(true);
      setErrorText(null);

      try {
        const response = await POST('admin/properties/units/update', unitDetails);

        if (response?.status) {
          setState({ ...newState, open: true });
          setSuccess(response.message);
          setUnitUpdateModalOpen(false);
          fetchPropertyDetails();
        } else {
          setErrorText(response.message);
        }
      } catch (err) {
        setErrorText(err);
      } finally {
        setConfirmLoading(false);
      }
    };

  const deleteUnit =
    (newState) =>
    async () => {
      const token = localStorage.getItem('token');
      setConfirmLoading(true);
      setErrorText(null);

      try {
        const url = `${BURL}/admin/unit`;
        const body = { propertyId: id, unitId, force: true };
        const response = await deleteRequest(url, body, token);

        setState({ ...newState, open: true });
        setSuccess(response.message);

        if (response?.status) {
          fetchPropertyDetails();
        } else {
          setErrorText(response.message);
        }
      } catch (err) {
        setErrorText(err);
      } finally {
        setConfirmLoading(false);
        setConfirmationOpen(false);
      }
    };

  const openEditResource = useCallback((r) => {
    setEditingResource(r);
    setResourceForm({
      order: r?.order ?? 0,
      description: r?.description ?? '',
      isCover: !!r?.isCover,
      isPublic: r?.isPublic ?? true,
    });
    setEditResourceOpen(true);
  }, []);

  const closeEditResource = useCallback(() => {
    setEditResourceOpen(false);
    setEditingResource(null);
  }, []);

  const patchResource = useCallback(async (resourceId, payload, t) => {
    return axios.patch(
      `${BURL}/admin/properties/resources/${resourceId}`,
      payload,
      { headers: { Authorization: `Bearer ${t}` } }
    );
  }, []);

  // ✅ NEW: delete resource request
  const deleteResource = useCallback(async (resourceId, t) => {
    return axios.delete(`${BURL}/admin/properties/resources/${resourceId}`, {
      headers: { Authorization: `Bearer ${t}` },
    });
  }, []);

  const updatePropertyResource = useCallback(async () => {
    if (!editingResource?.id) return;

    const t = localStorage.getItem('token');

    // Build payload (send only what changed)
    const payload = {};

    const newOrder = Number(resourceForm.order);
    const oldOrder = editingResource.order ?? 0;
    if (Number.isFinite(newOrder) && newOrder !== oldOrder) {
      payload.order = newOrder;
    }

    const newDesc = (resourceForm.description ?? '').trim();
    const oldDesc = (editingResource.description ?? '').trim();
    if (newDesc !== oldDesc) {
      payload.description = newDesc; // allow empty string to clear
    }

    const newPublic = !!resourceForm.isPublic;
    const oldPublic = editingResource.isPublic ?? true;
    if (newPublic !== oldPublic) {
      payload.isPublic = newPublic;
    }

    const newCover = !!resourceForm.isCover;
    const oldCover = !!editingResource.isCover;
    if (newCover !== oldCover) {
      payload.isCover = newCover;
    }

    if (Object.keys(payload).length === 0) {
      openToaster('info', 'No changes to save.');
      closeEditResource();
      return;
    }

    setSavingResource(true);
    try {
      if (payload.isCover === true) {
        const currentCover = resources.find((r) => r.isCover);
        if (currentCover && currentCover.id !== editingResource.id) {
          await patchResource(currentCover.id, { isCover: false }, t);
        }
      }

      await patchResource(editingResource.id, payload, t);

      openToaster('success', 'Image updated successfully');
      await fetchPropertyResources();

      // Optional: reset preview to reflect cover changes
      setPreviewMainResourceId(null);

      closeEditResource();
    } catch (err) {
      openToaster(
        'error',
        err?.response?.data?.message || 'Update failed. Please try again.'
      );
    } finally {
      setSavingResource(false);
    }
  }, [
    editingResource,
    resourceForm,
    resources,
    patchResource,
    fetchPropertyResources,
    openToaster,
    closeEditResource,
  ]);

  // ✅ NEW: open/close delete resource dialog
  const openDeleteResource = useCallback((r) => {
    setDeletingResource(r);
    setDeleteResourceOpen(true);
  }, []);

  const closeDeleteResource = useCallback(() => {
    setDeleteResourceOpen(false);
    setDeletingResource(null);
  }, []);

  // ✅ NEW: confirm delete resource handler
  const handleDeleteResource = useCallback(async () => {
    if (!deletingResource?.id) return;

    setDeletingResourceLoading(true);
    try {
      const t = localStorage.getItem('token');

      await deleteResource(deletingResource.id, t);

      openToaster('success', 'Image deleted successfully');

      // If the deleted resource was being previewed, reset preview
      setPreviewMainResourceId((prev) =>
        prev === deletingResource.id ? null : prev
      );

      // refresh resources
      await fetchPropertyResources();

      closeDeleteResource();
    } catch (err) {
      openToaster(
        'error',
        err?.response?.data?.message || 'Delete failed. Please try again.'
      );
    } finally {
      setDeletingResourceLoading(false);
    }
  }, [
    deletingResource,
    deleteResource,
    openToaster,
    fetchPropertyResources,
    closeDeleteResource,
  ]);

  const loader = <SmallLoader />;

  return (
    <>
      {loading && loader}

      {errorText && !loading && (
        <div className="px-5 md:px-10 mt-4">
          <p className="text-red-600">{errorText}</p>
          <Button
            onClick={() =>
              Promise.all([fetchPropertyDetails(), fetchPropertyResources()])
            }
          >
            Try again
          </Button>
        </div>
      )}

      <div className="px-5 md:px-10 sm:px-3 flex flex-col gap-10 mt-5 md:mt-4 pt-3 bg-white mb-0">
        <div className="px-5 md:px-10 sm:px-3 flex flex-col gap-10 mt-5 md:mt-4 pt-3 bg-white mb-0">
          {/* Header */}
          <div className="w-full text-[#6C6C6C] flex flex-col md:flex-row justify-between gap-3">
            <div className="flex gap-3 items-center">
              <Link
                to={'/dashboard/properties'}
                className="font-[600] text-[18px] md:text-[22px] cursor-pointer"
              >
                Properties
              </Link>
              <span className="text-[12px] md:text-[14px]">
                {' '}
                &gt; Property Details
              </span>
            </div>

            {!isEditing ? (
              <div
                className="flex justify-end md:justify-start items-center gap-2 cursor-pointer"
                onClick={() => setIsEditing(true)}
              >
                <EditIcon className="text-[#6C6C6C]" sx={{ fontSize: 18 }} />
                <span className="hidden md:flex text-[14px]">Edit</span>
              </div>
            ) : (
              <div className="flex justify-end mt-2 md:mt-0">
                <button
                  onClick={onSaveProperty}
                  className="cursor-pointer text-[#0FA958] font-[600]"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>

          {/* Property Details */}
          <div className="w-full mt-6 pb-12 border-b border-black/40">
            <div className="grid grid-cols-1 md:grid-cols-[260px,1fr] gap-6 items-start">
              {/* Image column */}
              <div className="hidden md:block">
                <div className="flex flex-col gap-2">
                  {/* Cover image (always 1 slot) */}
                  <div className="relative">
                    <img
                      src={mainImageSrc}
                      alt="Property"
                      className="w-full h-[280px] rounded-md object-cover"
                    />
                    {coverSlotIndex >= 0 && !imagePreview && previewIndex < 0 && (
                      <span className="absolute top-2 left-2 bg-white/90 text-[#0FA958] text-xs font-semibold px-2 py-1 rounded">
                        Cover
                      </span>
                    )}
                  </div>

                  {/* Edit + Delete buttons for main image */}
                  {currentMainResource && (
                    <div className="flex gap-2">
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => openEditResource(currentMainResource)}
                        className="!mt-1"
                      >
                        Edit image
                      </Button>

                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => openDeleteResource(currentMainResource)}
                        className="!mt-1"
                      >
                        Delete
                      </Button>
                    </div>
                  )}

                  {/* Thumbnails (always 3 slots; cover never appears here) */}
                  <div className="flex gap-2">
                    {thumbSlots.map((r, idx) => {
                      // Placeholder slot
                      if (!r) {
                        return (
                          <div
                            key={`placeholder-${idx}`}
                            onClick={() => fileInputRef.current?.click()}
                            className="w-[68px] h-[65px] rounded-md border border-dashed border-[#0FA958] bg-[#0FA95814] flex items-center justify-center text-[10px] text-[#6C6C6C] text-center px-1 cursor-pointer hover:bg-[#0FA95822]"
                            title="Upload another image"
                          >
                            + Add
                          </div>
                        );
                      }

                      // Active highlight: if user is previewing a thumbnail, highlight that thumb
                      const isActive = previewMainResourceId === r.id;

                      return (
                        <div key={r.id} className="relative">
                          <img
                            src={r.resource}
                            alt="thumb"
                            onClick={() => {
                              // clicking thumb previews it as the main image
                              setPreviewMainResourceId(r.id);
                            }}
                            className={`w-[68px] h-[65px] rounded-md object-cover cursor-pointer ${
                              isActive ? 'ring-2 ring-[#0FA958]' : ''
                            }`}
                          />

                          {/* NEW: thumbnail delete button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteResource(r);
                            }}
                            className="absolute top-1 left-1 bg-white/90 rounded p-1"
                            title="Delete image"
                          >
                            <DeleteIcon
                              sx={{ fontSize: 14 }}
                              className="text-red-600"
                            />
                          </button>

                          {/* thumbnail edit button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditResource(r);
                            }}
                            className="absolute top-1 right-1 bg-white/90 rounded p-1"
                            title="Edit image"
                          >
                            <EditIcon
                              sx={{ fontSize: 14 }}
                              className="text-[#6C6C6C]"
                            />
                          </button>

                          {/* Optional badge if this is cover (shouldn't happen in thumbs because we exclude cover) */}
                          {r.isCover && (
                            <span className="absolute bottom-1 left-1 bg-white/90 text-[#0FA958] text-[10px] font-semibold px-1.5 py-0.5 rounded">
                              Cover
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* file picker */}
                  <label htmlFor="property_image" className="cursor-pointer">
                    <p className="bg-[#0FA95833] px-2 py-3 text-[#6C6C6C] text-sm w-full text-center rounded-md">
                      Add/Edit Images
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={loadImage}
                      id="property_image"
                      className="hidden"
                    />
                  </label>

                  {/* upload */}
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={!selectedImageFile || uploadingImage}
                    onClick={uploadPropertyImage}
                  >
                    {uploadingImage ? 'Uploading...' : 'Upload Image'}
                  </Button>
                </div>
              </div>

              {/* Fields */}
              <div className="w-full flex flex-col gap-5 md:gap-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2 text-[#6C6C6C] text-[12px] md:text-[14px] font-[300]">
                    <label>Property Name</label>
                    <input
                      type="text"
                      disabled={!isEditing}
                      className="disabled:text-black disabled:bg-gray-200 disabled:border-gray-200 border-[0.75px] border-[#0FA958] p-3 outline-none focus:outline-none focus:border-2 rounded-[5px] capitalize"
                      name="name"
                      value={updateProperty?.name}
                      onChange={handleUpdateChange}
                    />
                  </div>

                  <div className="flex flex-col gap-2 text-[#6C6C6C] text-[12px] md:text-[14px] font-[300]">
                    <label>Property Type</label>
                    <select
                      name="type"
                      disabled={!isEditing}
                      className="disabled:text-black disabled:bg-gray-200 disabled:border-gray-200 border-[0.75px] border-[#0FA958] px-3 py-4 outline-none focus:outline-none focus:border-2 rounded-[5px] capitalize"
                      value={updateProperty?.type}
                      onChange={handleUpdateChange}
                    >
                      {typeList.map((type) => (
                        <option key={type} value={type || ''}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2 text-[#6C6C6C] text-[12px] md:text-[14px] font-[300]">
                    <label>State</label>
                    <select
                      name="state"
                      disabled={!isEditing}
                      className="disabled:text-black disabled:bg-gray-200 disabled:border-gray-200 border-[0.75px] border-[#0FA958] px-3 py-4 outline-none focus:outline-none focus:border-2 rounded-[5px] capitalize"
                      value={updateProperty.state}
                      onChange={handleUpdateChange}
                    >
                      <option value="Abia">Abia</option>
                      <option value="Adamawa">Adamawa</option>
                      <option value="Ondo">Ondo</option>
                      <option value="Osun">Osun</option>
                      <option value="Oyo">Oyo</option>
                      <option value="Ogun">Ogun</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-2 text-[#6C6C6C] text-[12px] md:text-[14px] font-[300]">
                    <label>Street Address</label>
                    <input
                      type="text"
                      disabled={!isEditing}
                      className="disabled:text-black disabled:bg-gray-200 disabled:border-gray-200 border-[0.75px] border-[#0FA958] p-3 outline-none focus:outline-none focus:border-2 rounded-[5px] capitalize"
                      name="address"
                      value={updateProperty.address}
                      onChange={handleUpdateChange}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2 text-[#6C6C6C] text-[12px] md:text-[14px] font-[300]">
                    <label>Country</label>
                    <select
                      disabled={!isEditing}
                      className="disabled:text-black disabled:bg-gray-200 disabled:border-gray-200 border-[0.75px] border-[#0FA958] px-3 py-4 outline-none focus:outline-none focus:border-2 rounded-[5px] capitalize"
                      name="country"
                      value={updateProperty.country}
                      onChange={handleUpdateChange}
                    >
                      <option value="">Select country</option>
                      <option value="nigeria">Nigeria</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-2 text-[#6C6C6C] text-[12px] md:text-[14px] font-[300]">
                    <label>City</label>
                    <input
                      type="text"
                      disabled={!isEditing}
                      className="disabled:text-black disabled:bg-gray-200 disabled:border-gray-200 border-[0.75px] border-[#0FA958] p-3 outline-none focus:outline-none focus:border-2 rounded-[5px] capitalize"
                      name="city"
                      value={updateProperty.city}
                      onChange={handleUpdateChange}
                    />
                  </div>
                </div>

                {/* keep rest of your component below as-is */}
                <div className="mt-2">
                  <Button
                    onClick={() => setDialog('ADD_ADMIN')}
                    className="!bg-[#0FA958] !text-white"
                    startIcon={<AddIcon />}
                  >
                    Add Admin
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Property Managers */}
          <Dialog
            fullWidth
            maxWidth="sm"
            open={modalOpen}
            TransitionComponent={Transition}
            keepMounted
            aria-describedby="alert-dialog-slide-description"
            onClose={handleClose}
          >
            <DialogContent>
              <DialogContentText id="alert-dialog-slide-description">
                <HorizontalLinearAssignAdminStepper
                  open={modalOpen}
                  setOpen={setModalOpen}
                  fetchPropertyDetails={fetchPropertyDetails}
                  currentPropertyId={id}
                />
              </DialogContentText>
            </DialogContent>
          </Dialog>

          <div className="flex flex-col gap-4 pb-[2rem] border-b-[0.5px] border-[#000000]">
            <div className="w-full text-[#6C6C6C] flex justify-between">
              <div className="flex gap-3">
                <h1 className="font-[600] text-[18px] md:text-[22px] cursor-pointer">
                  Property Managers
                </h1>
              </div>
              <div
                onClick={() => setModalOpen(true)}
                className="px-5 py-2 bg-austel-green hover:bg-[#0d964d] hover:font-semibold flex items-center gap-2 !rounded-full text-white transition-all ease-in duration-500 cursor-pointer"
              >
                <AddIcon
                  className="flex items-center text-white"
                  sx={{ fontSize: '5' }}
                />
                <span className="hidden md:flex text-[14px]">Add Admin</span>
              </div>
            </div>

            {property?.adminPropertyRoles ? (
              <div className="w-full flex flex-col gap-5 md:gap-8">
                {property.adminPropertyRoles.map((role, i) => (
                  <div key={i} className="w-full grid md:flex gap-5">
                    <div className="flex flex-col gap-2 w-full md:w-[60%] text-[#6C6C6C] text-[12px] md:text-[14px] font-[300] cursor-pointer">
                      <label htmlFor="" className="">
                        Admin Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        disabled={editableIndex !== i}
                        onChange={(e) => handleAdminDataChange(e, i)}
                        className="disabled:text-black disabled:bg-gray-200 disabled:border-gray-200 border-[0.75px] border-[#0FA958] p-3 outline-none focus:outline-none focus:border-2 rounded-[5px] capitalize"
                        placeholder="Admin Name"
                        value={role.admin.user.firstname}
                      />
                    </div>

                    <div className="flex flex-col gap-2 w-full md:w-[30%] text-[#6C6C6C] text-[12px] md:text-[14px] font-[300]">
                      <label htmlFor="">Role/Permission</label>
                      <select
                        name="role"
                        disabled={editableIndex !== i}
                        onChange={(e) => handleAdminDataChange(e, i)}
                        className="disabled:text-black disabled:bg-gray-200 disabled:border-gray-200 border-[0.75px] border-[#0FA958] px-3 py-4 outline-none focus:outline-none focus:border-2 rounded-[5px] capitalize"
                        id=""
                        placeholder="Admin Role"
                        value={role.role.id}
                      >
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="my_property hidden w-full md:w-[10%] md:flex md:gap-4 justify-center items-center mt-5 ml-3">
                      {editableIndex === i ? (
                        <>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleAdminUpdate(i)}
                          >
                            {!confirmLoading ? <span>Save</span> : loader}
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setEditableIndex(null)}
                            className="!bg-red-600"
                          >
                            Close
                          </Button>
                        </>
                      ) : (
                        <div className="flex gap-3">
                          <span
                            title="Edit admin details"
                            className="cursor-pointer"
                          >
                            <EditIcon
                              onClick={() => setEditableIndex(i)}
                              className="flex items-center cursor-pointer text-[#6C6C6C]"
                              sx={{ fontSize: '14' }}
                            />
                          </span>
                          <span title="Remove admin" className="cursor-pointer">
                            <DeleteIcon
                              onClick={() =>
                                handleAdminRemovalConfirmation(
                                  role.adminPropertyRoleId
                                )
                              }
                              className="flex items-center cursor-pointer text-red-600"
                              sx={{ fontSize: '14' }}
                            />
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>No admin roles available</p>
            )}
          </div>

          {/* Unit Details */}
          {property?.units ? (
            <div className="flex flex-col gap-4 pb-[2rem]">
              <div className="w-full text-[#6C6C6C] flex justify-between">
                <div className="flex gap-3">
                  <h1 className="font-[600] text-[18px] md:text-[22px] cursor-pointer">
                    Unit Details
                  </h1>
                </div>
                <div
                  onClick={() => setUnitModalOpen(true)}
                  className="flex gap-2 items-center cursor-pointer"
                >
                  <AddIcon
                    className="flex items-center cursor-pointer text-[#6C6C6C]"
                    sx={{ fontSize: '5' }}
                  />
                  <span className="hidden md:flex text-[14px]">Add Unit</span>
                </div>
              </div>

              {/* Unit Table */}
              <div className="overflow-x-auto my-table w-[100%] lg:w-[99%] xl:w-full">
                <table className="table-auto w-full text-[#6C6C6C]">
                  <thead>
                    <tr>
                      <th>Unit Name</th>
                      <th>No Of Room</th>
                      <th>Status</th>
                      <th>Tenants</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {property.units.map((unit, index) => (
                      <tr
                        key={unit.name}
                        className="mb-[30rem] mt-20 border-b-8 border-white"
                      >
                        <td className="capitalize font-bold text-[#0FA958]">
                          {unit.name}
                        </td>
                        <td className="capitalize">{unit.roomCount}</td>
                        <td className="capitalize">
                          <div className="flex flex-col gap-0 lg:gap-1">
                            <div className="flex gap-5">
                              <div className="flex gap-1 items-center">
                                <div className="w-[8px] h-[8px] bg-[#0C9A00] rounded-full flex items-center justify-items-center"></div>
                                <span className="text:[12px] md:text-[14px]">
                                  Active
                                </span>
                              </div>
                              <span>{unit.occupiedRooms}</span>
                            </div>
                            <div className="flex gap-3">
                              <div className="flex gap-1 items-center">
                                <div className="w-[8px] h-[8px] bg-[#EB042D] rounded-full flex items-center justify-items-center"></div>
                                <span className="">Vacant</span>
                              </div>
                              <span>{unit.emptyRooms}</span>
                            </div>
                          </div>
                        </td>
                        <td className="capitalize">
                          {unit.tenantCount} Tenant(s)
                        </td>
                        <td className="capitalize">
                          {formatDate(unit.createdAt)}
                        </td>
                        <td className="flex gap-5 items-center mt-3 md:mt-3">
                          <Link to={`/dashboard/property/${id}/unit/${unit.id}`}>
                            <span
                              title="View unit details"
                              className="cursor-pointer"
                            >
                              <RemoveRedEye
                                className="flex items-center cursor-pointer text-[#6C6C6C]"
                                sx={{ fontSize: '18px' }}
                              />
                            </span>
                          </Link>

                          <span
                            title="Edit unit details"
                            className="cursor-pointer"
                          >
                            <EditIcon
                              className="flex items-center cursor-pointer text-[#6C6C6C]"
                              sx={{ fontSize: '18px' }}
                              onClick={() => handleUnitUpdateModal(unit)}
                            />
                          </span>

                          <span title="Delete unit" className="cursor-pointer">
                            <DeleteIcon
                              className="flex items-center cursor-pointer text-red-600"
                              sx={{ fontSize: '18px' }}
                              onClick={() => handleConfirmation(unit.id)}
                            />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p>No Unit available</p>
          )}

          {/* New Unit Modal */}
          <Dialog
            fullWidth
            maxWidth="sm"
            open={unitModalOpen}
            onClose={handleClose}
          >
            <DialogTitle>Add Unit</DialogTitle>
            <DialogContent>
              <div className="pt-5 gap-2 flex flex-col">
                <div className="flex flex-col md:flex-row gap-3 md:gap-2">
                  <div className="w-full md:w-[50%]">
                    <TextField
                      fullWidth
                      name="name"
                      label="Name"
                      type="text"
                      value={values.name}
                      onChange={handleUnitChange}
                      variant="outlined"
                    />
                  </div>
                  <div className="w-full md:w-[50%]">
                    <TextField
                      fullWidth
                      name="description"
                      label="Description"
                      type="text"
                      value={values.description}
                      onChange={handleUnitChange}
                      variant="outlined"
                    />
                  </div>
                </div>
                {errorText !== '' && (
                  <span className="text-red-600 mt-1">{errorText}</span>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button onClick={addNewUnit}>
                {!confirmLoading && <span>Save</span>}
                {confirmLoading && loader}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Update Unit Modal */}
          <Dialog
            fullWidth
            maxWidth="sm"
            open={unitUpdateModalOpen}
            onClose={handleClose}
          >
            <DialogTitle>Update Unit</DialogTitle>
            <DialogContent>
              <div className="pt-5 gap-2 flex flex-col">
                <div className="flex gap-2">
                  <div className="w-[50%]">
                    <TextField
                      fullWidth
                      name="name"
                      label="Name"
                      type="text"
                      value={unitDetails.name}
                      onChange={handleUnitChange}
                      variant="outlined"
                    />
                  </div>
                  <div className="w-[50%]">
                    <TextField
                      fullWidth
                      name="description"
                      label="Description"
                      type="text"
                      value={unitDetails.description}
                      onChange={handleUnitChange}
                      variant="outlined"
                    />
                  </div>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                onClick={updateUnit({ vertical: 'bottom', horizontal: 'right' })}
              >
                {!confirmLoading && <span>Save</span>}
                {confirmLoading && loader}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>

      {/* ADD ADMIN dialog */}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={dialog === 'ADD_ADMIN'}
        TransitionComponent={Transition}
        keepMounted
        onClose={closeAllDialogs}
      >
        <DialogContent>
          <DialogContentText>
            <HorizontalLinearAssignAdminStepper
              open={dialog === 'ADD_ADMIN'}
              setOpen={(v) => setDialog(v ? 'ADD_ADMIN' : null)}
              fetchPropertyDetails={fetchPropertyDetails}
              currentPropertyId={id}
            />
          </DialogContentText>
        </DialogContent>
      </Dialog>

      {/* Delete Units */}
      <Dialog
        open={confirmationOpen}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Delete unit?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this unit?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmationOpen(false)}>No</Button>
          <Button
            sx={{ color: 'red' }}
            onClick={deleteUnit({ vertical: 'bottom', horizontal: 'right' })}
            autoFocus
            className="text-[#BD2424]"
          >
            {!confirmLoading && <span>Yes</span>}
            {confirmLoading && loader}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin removal confirmation Dialog */}
      <Dialog
        open={removalConfirmationOpen}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Remove Admin?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to remove this admin?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovalConfirmationOpen(false)}>No</Button>
          <Button
            sx={{ color: 'red' }}
            onClick={handleRemoveAdmin}
            autoFocus
            className="text-[#BD2424]"
          >
            {!confirmLoading ? <span>Yes</span> : loader}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Resource Dialog */}
      <Dialog
        open={editResourceOpen}
        onClose={closeEditResource}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
      >
        <DialogTitle>Edit Image</DialogTitle>

        <DialogContent>
          <div className="flex flex-col gap-4 pt-2">
            {editingResource?.resource && (
              <img
                src={editingResource.resource}
                alt="editing"
                className="w-full h-[220px] object-cover rounded-md"
              />
            )}

            <TextField
              label="Order"
              type="number"
              value={resourceForm.order}
              onChange={(e) =>
                setResourceForm((p) => ({ ...p, order: e.target.value }))
              }
              fullWidth
            />

            <TextField
              label="Description"
              value={resourceForm.description}
              onChange={(e) =>
                setResourceForm((p) => ({ ...p, description: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={resourceForm.isCover}
                  onChange={(e) =>
                    setResourceForm((p) => ({
                      ...p,
                      isCover: e.target.checked,
                    }))
                  }
                />
                Set as cover
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={resourceForm.isPublic}
                  onChange={(e) =>
                    setResourceForm((p) => ({
                      ...p,
                      isPublic: e.target.checked,
                    }))
                  }
                />
                Public
              </label>
            </div>
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeEditResource} disabled={savingResource}>
            Cancel
          </Button>
          <Button
            onClick={updatePropertyResource}
            disabled={savingResource}
            variant="contained"
          >
            {savingResource ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ NEW: Delete Resource Confirmation Dialog */}
      <Dialog
        open={deleteResourceOpen}
        onClose={closeDeleteResource}
        aria-labelledby="delete-resource-title"
        aria-describedby="delete-resource-description"
      >
        <DialogTitle id="delete-resource-title">Delete image?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-resource-description">
            Are you sure you want to delete this image? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteResource} disabled={deletingResourceLoading}>
            Cancel
          </Button>
          <Button
            sx={{ color: 'red' }}
            onClick={handleDeleteResource}
            disabled={deletingResourceLoading}
          >
            {deletingResourceLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toaster */}
      <Snackbar
        open={showToaster}
        autoHideDuration={6000}
        onClose={handleToasterClose}
        anchorOrigin={snackbarAnchor}
      >
        <Alert
          onClose={handleToasterClose}
          severity={toasterType}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default PropertyDetails;
